import React, {
  useEffect,
  useMemo,
  Suspense,
  LazyExoticComponent,
} from "react";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";

import { Layout } from "./components/Layout/Layout";
import { ROUTES } from "./configs/routes";
import { auth, nonAuth, unsupportedBrowserOnly } from "./services/auth";
import ScrollToTop from "./components/__hoc/ScrollToTopHoc";
import { INVALID_PAGE } from "./constants/storage";
import { FeatureToggle } from "./utils/feature-toggle/FeatureToggle";
import DeepLinkChecker from "./components/__hoc/DeepLinkChecker";
import { EmailSupport } from "./utils/email-supported/EmailSupport";
import withRenderTimeLogger from "./components/__hoc/RenderTimeLogger";
import withFontDetection from "./components/__hoc/withFontDetection";
import AnalyticsTracker from "./components/AnalyticsTracker/AnalyticsTracker";
import LoadingFallback from "./components/LoadingFallback/LoadingFallback";

// Lazy-loaded page components for code splitting
// Error Pages - loaded on demand
const Error400 = React.lazy(() =>
  import("./pages/nonauth/ErrorPages/Error400").then((module) => ({
    default: module.Error400,
  }))
);
const Error403 = React.lazy(() =>
  import("./pages/nonauth/ErrorPages/Error403").then((module) => ({
    default: module.Error403,
  }))
);
const Error404 = React.lazy(() =>
  import("./pages/nonauth/ErrorPages/Error404").then((module) => ({
    default: module.Error404,
  }))
);
const Error500 = React.lazy(() =>
  import("./pages/nonauth/ErrorPages/Error500").then((module) => ({
    default: module.Error500,
  }))
);
const Error502 = React.lazy(() =>
  import("./pages/nonauth/ErrorPages/Error502").then((module) => ({
    default: module.Error502,
  }))
);
const Error503 = React.lazy(() =>
  import("./pages/nonauth/ErrorPages/Error503").then((module) => ({
    default: module.Error503,
  }))
);
const ErrorIE = React.lazy(() =>
  import("./pages/nonauth/ErrorPages/ErrorIE").then((module) => ({
    default: module.ErrorIE,
  }))
);
const ErrorMaintenance = React.lazy(() =>
  import("./pages/nonauth/ErrorPages/ErrorMaintenance").then((module) => ({
    default: module.ErrorMaintenance,
  }))
);

// Auth pages - loaded on demand
const MyData = React.lazy(() =>
  import("./pages/auth/MyData/MyData").then((module) => ({
    default: module.MyData,
  }))
);
const History = React.lazy(() =>
  import("./pages/auth/MyData/History/History").then((module) => ({
    default: module.History,
  }))
);
const ADSSOLogout = React.lazy(() =>
  import("./pages/auth/ADSSOLogout/ADSSOLogout").then((module) => ({
    default: module.default,
  }))
);

// Non-auth pages - loaded on demand
const Login = React.lazy(() =>
  import("./pages/nonauth/Login/Login").then((module) => ({
    default: module.Login,
  }))
);
const LandingPage = React.lazy(() =>
  import("./pages/nonauth/LandingPage/LandingPage").then((module) => ({
    default: module.default,
  }))
);
const PrivacyPolicyOverview = React.lazy(() =>
  import("./pages/nonauth/PrivacyPolicy/Overview").then((module) => ({
    default: module.default,
  }))
);
const PrivacyPolicy = React.lazy(() =>
  import("./pages/nonauth/PrivacyPolicy/PrivacyPolicy").then((module) => ({
    default: module.PrivacyPolicy,
  }))
);
const CookiePolicy = React.lazy(() =>
  import("./pages/nonauth/CookiePolicy/CookiePolicy").then((module) => ({
    default: module.CookiePolicy,
  }))
);
const GlobalRightsResponse = React.lazy(() =>
  import("./pages/nonauth/GlobalRightsResponse/GlobalRightsResponse").then(
    (module) => ({
      default: module.GlobalRightsResponse,
    })
  )
);
const DigitalLegacyAuth = React.lazy(() =>
  import("./pages/nonauth/DigitalLegacyAuth/DigitalLegacyAuth").then(
    (module) => ({
      default: module.default,
    })
  )
);

// Consignment pages - loaded on demand
const BdpConsignment = React.lazy(() =>
  import("./pages/nonauth/Consignment/BdpConsignment").then((module) => ({
    default: module.default,
  }))
);
const AnalyticsConsignment = React.lazy(() =>
  import("./pages/nonauth/Consignment/AnalyticsConsignment").then((module) => ({
    default: module.default,
  }))
);
const DiagmonConsignment = React.lazy(() =>
  import("./pages/nonauth/Consignment/DiagmonConsignment").then((module) => ({
    default: module.default,
  }))
);

// Helper component to wrap lazy-loaded components with Suspense
const LazyRoute: React.FC<{
  component: LazyExoticComponent<React.ComponentType<any>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  wrapper?: (component: React.ComponentType<any>) => React.ComponentType<any>;
}> = ({ component: LazyComponent, wrapper }) => {
  const WrappedComponent = wrapper
    ? wrapper(
        React.forwardRef((props, ref) => <LazyComponent {...props} ref={ref} />)
      )
    : LazyComponent;

  return (
    <Suspense fallback={<LoadingFallback />}>
      <WrappedComponent />
    </Suspense>
  );
};

const AppRouter = () => {
  // Calculate the base path dynamically
  const basename = useMemo(() => {
    // If REACT_APP_ROUTER_BASENAME is set to a non-root path (from build time), use it
    // We exclude "/" because we want to handle root deployments at runtime
    if (
      process.env.REACT_APP_ROUTER_BASENAME &&
      process.env.REACT_APP_ROUTER_BASENAME !== "/"
    )
      return process.env.REACT_APP_ROUTER_BASENAME;

    // Otherwise, calculate it from the current URL at runtime
    const { pathname } = window.location;

    // If pathname contains 'preview', extract the preview path
    const previewMatch = pathname.match(/^\/preview\/(\d+)/);
    if (previewMatch) return `/preview/${previewMatch[1]}`;

    // Default to root
    return "/";
  }, []);

  // Get space-separated list of disabled features and convert to an array of string
  const envDisabledFeatures =
    process.env.REACT_APP_DISABLED_FEATURES?.split(" ") || [];

  const supportedCountries: string[] = JSON.parse(
    process.env.REACT_APP_EMAIL_SUPPORTED_COUNTRIES as string
  ).supportedCountries;

  useEffect(() => {
    // need to add this during mount because 404 page doesn't unmount when the user try to deeplink from 404 page to other valid page
    sessionStorage.removeItem(INVALID_PAGE);
  }, []);

  return (
    <Router basename={basename}>
      <AnalyticsTracker />
      <FeatureToggle disabledFeatures={envDisabledFeatures}>
        <EmailSupport supportedCountries={supportedCountries}>
          <DeepLinkChecker>
            <Layout>
              <ScrollToTop>
                <Suspense fallback={<LoadingFallback />}>
                  <Switch>
                    <Route
                      exact
                      path={ROUTES.ROOT}
                      component={nonAuth(withRenderTimeLogger(LandingPage))}
                    />
                    <Route
                      exact
                      path={ROUTES.LOGIN}
                      component={withRenderTimeLogger(Login)}
                    />
                    <Route
                      exact
                      path={ROUTES.ERROR_400}
                      component={withRenderTimeLogger(Error400)}
                    />
                    <Route
                      exact
                      path={ROUTES.ERROR_403}
                      component={withRenderTimeLogger(Error403)}
                    />

                    <Route
                      exact
                      path={ROUTES.ERROR_500}
                      component={withRenderTimeLogger(Error500)}
                    />
                    <Route
                      exact
                      path={ROUTES.ERROR_502}
                      component={withRenderTimeLogger(Error502)}
                    />
                    <Route
                      exact
                      path={ROUTES.ERROR_503}
                      component={withRenderTimeLogger(Error503)}
                    />
                    <Route
                      exact
                      path={ROUTES.ERROR_IE}
                      component={unsupportedBrowserOnly(
                        withRenderTimeLogger(ErrorIE)
                      )}
                    />
                    <Route
                      exact
                      path={ROUTES.ERROR_MAINTENANCE}
                      component={withRenderTimeLogger(ErrorMaintenance)}
                    />
                    <Route
                      exact
                      path={ROUTES.MYDATA_HISTORY}
                      component={auth(withRenderTimeLogger(History))}
                    />
                    <Route
                      exact
                      path={ROUTES.MYDATA}
                      component={auth(withRenderTimeLogger(MyData))}
                    />
                    <Route
                      exact
                      path={ROUTES.OLD_DOWNLOAD_HISTORY}
                      component={auth(withRenderTimeLogger(MyData))}
                    />
                    <Route
                      exact
                      path={ROUTES.OLD_DELETE_HISTORY}
                      component={auth(withRenderTimeLogger(MyData))}
                    />
                    <Route
                      exact
                      path={ROUTES.OLD_OPTOUT_HISTORY}
                      component={auth(withRenderTimeLogger(MyData))}
                    />
                    <Route
                      exact
                      path={ROUTES.OLD_DOWNLOAD_REQUEST}
                      component={auth(withRenderTimeLogger(MyData))}
                    />
                    <Route
                      exact
                      path={ROUTES.OLD_DELETE_REQUEST}
                      component={auth(withRenderTimeLogger(MyData))}
                    />
                    <Route
                      exact
                      path={ROUTES.OLD_OPTOUT_REQUEST}
                      component={auth(withRenderTimeLogger(MyData))}
                    />
                    <Route
                      exact
                      path={ROUTES.POLICY_OVERVIEW}
                      component={nonAuth(PrivacyPolicyOverview)}
                    />
                    <Route
                      exact
                      path={ROUTES.SUMMARY_REQUEST_DETAILS}
                      component={auth(withRenderTimeLogger(MyData))}
                    />
                    <Route
                      exact
                      path={ROUTES.MYDATA_HISTORY_REQUEST_DETAILS}
                      component={auth(withRenderTimeLogger(History))}
                    />
                    <Route
                      exact
                      path={ROUTES.OLD_REQUEST_DETAILS}
                      component={auth(withRenderTimeLogger(History))}
                    />
                    <Route
                      exact
                      path={ROUTES.PRIVACY_POLICY}
                      component={nonAuth(withRenderTimeLogger(PrivacyPolicy))}
                    />

                    <Route
                      exact
                      path={ROUTES.COOKIE_POLICY}
                      component={nonAuth(withRenderTimeLogger(CookiePolicy))}
                    />

                    <Route
                      exact
                      path={ROUTES.ABOUT_GRPS}
                      component={nonAuth(
                        withRenderTimeLogger(GlobalRightsResponse)
                      )}
                    />
                    <Route
                      exact
                      path={[
                        ROUTES.OLD_CONSIGNMENT_WITH_KR,
                        ROUTES.OLD_CONSIGNMENT,
                        ROUTES.BDP_CONSIGNMENT,
                      ]}
                      component={nonAuth(withRenderTimeLogger(BdpConsignment))}
                    />
                    <Route
                      exact
                      path={ROUTES.ANALYTICS_CONSIGNMENT}
                      component={nonAuth(
                        withRenderTimeLogger(AnalyticsConsignment)
                      )}
                    />
                    <Route
                      exact
                      path={ROUTES.DIAGMON_CONSIGNMENT}
                      component={nonAuth(
                        withRenderTimeLogger(DiagmonConsignment)
                      )}
                    />
                    <Route
                      exact
                      path={ROUTES.DIGITAL_LEGACY_AUTH}
                      component={nonAuth(
                        withRenderTimeLogger(DigitalLegacyAuth)
                      )}
                    />
                    <Route
                      exact
                      path={ROUTES.ADSSO_LOGOUT}
                      component={nonAuth(withRenderTimeLogger(ADSSOLogout))}
                    />
                    <Route component={withRenderTimeLogger(Error404)} />
                  </Switch>
                </Suspense>
              </ScrollToTop>
            </Layout>
          </DeepLinkChecker>
        </EmailSupport>
      </FeatureToggle>
    </Router>
  );
};

export default withFontDetection(AppRouter, "AppRouter");
